import { Injectable } from '@angular/core';
import { LMContextService } from './lm-context.service';
import { KeyedResource, Resource } from '@resources/index';
import { LMUrlUtilService } from './utils';
import * as _ from 'lodash';
import { BIDrillResource } from '@resources/bidrill.resource';
import { AppConstants } from '@utils/app.constants';
import { LMLoggerService } from '@services/lm-logger.service';

@Injectable()
export class LMUrlInfoBuilderService {
  private static ServerLogVersionId = 'LMCLIENT UI ALERT: 2023.02';
  private static PrefixString = 'SPECURL DIFF';

  constructor(
    private lmContextService: LMContextService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmLoggerService: LMLoggerService
  ) {}

  public getPageUrlInfoFromHttpUrl(httpUrl: string): LMPageUrlInfo {
    return this.getBaseUrlInfoFromHttpUrl(httpUrl) as LMPageUrlInfo;
  }

  public getListUrlInfoFromHttpUrl(httpUrl: string): LMListUrlInfo {
    return this.getBaseUrlInfoFromHttpUrl(httpUrl) as LMFormUrlInfo;
  }

  public getListUrlInfoFromListSpec(listSpec: LMListSpec, options?: LMListUrlInfoOptions): LMListUrlInfo {
    return this.getBaseUrlInfoFromListSpec(listSpec, options) as LMListUrlInfo;
  }

  public getFormUrlInfoFromHttpUrl(httpUrl: string): LMFormUrlInfo {
    return this.getBaseUrlInfoFromHttpUrl(httpUrl) as LMFormUrlInfo;
  }

  public getFormUrlInfoFromFormSpec(formSpec: LMFormSpec, options?: LMFormUrlInfoOptions): LMFormUrlInfo {
    return this.getBaseUrlInfoFromFormSpec(formSpec, options) as LMFormUrlInfo;
  }

  public fillOutPageUrls(pageUrlInfo: LMPageUrlInfo, pageSpec: LMPageSpec): void {
    pageUrlInfo.contextSessionKeys = { ...pageSpec.contextSessionKeys };
    pageUrlInfo.initialDataUrl = this.lmUrlUtilService.buildPageInitialDataUrl(pageUrlInfo, pageSpec);
  }

  public fillOutListUrls(listUrlInfo: LMListUrlInfo, listSpec: LMListSpec, options?: LMListUrlInfoOptions): void {
    // crucial for mingle drill back message that contain extra valid csks, especially for csk.gen.
    // todo: need to include nc. value as well. Maybe rename this to extraParams.
    listUrlInfo.contextSessionKeys = { ...listSpec.contextSessionKeys };

    if (!this.lmContextService.useNewSpecUrls()) {
      this.compareSpecPart(listUrlInfo, 'resourceId', listSpec.resourceId, listUrlInfo.resource.resourceId);

      if (listUrlInfo.sourceResource || listSpec.sourceResourceType) {
        this.compareSpecPart(listUrlInfo, 'sourceResourceType', listSpec.sourceResourceType, listUrlInfo?.sourceResource?.resourceType);
        this.compareSpecPart(listUrlInfo, 'sourceResourceId', listSpec.sourceResourceId, listUrlInfo?.sourceResource?.resourceId);
      }
    }

    listUrlInfo.initialDataUrl = this.lmUrlUtilService.buildListInitialDataUrl(listUrlInfo, listSpec, options);
    listUrlInfo.transactionUrl = this.lmUrlUtilService.buildListTransactionUrl(listUrlInfo, listSpec);
    listUrlInfo.viewAuditLogRedirectUrl = this.lmUrlUtilService.buildViewAuditLogRedirectUrl(listUrlInfo);
    listUrlInfo.navigationUrl = this.lmUrlUtilService.buildNavigationUrl(listUrlInfo);

    if (listSpec.busListSpec.implementsTreeView && !listSpec.busListSpec.implementsCubeView) {
      listUrlInfo.childrenUrl = this.lmUrlUtilService.buildHierarchyChildrenUrl(listUrlInfo);
      if (listSpec.busListSpec.implementsOrgChartView) {
        listUrlInfo.centeredUrl = this.lmUrlUtilService.buildHierarchyCenteredUrl(listUrlInfo);
      }
    }

    listUrlInfo.actionRedirectUrl = this.lmUrlUtilService.buildActionRedirectUrl(listUrlInfo, listSpec.isTemplateList);
    listUrlInfo.openActionRedirectUrl = this.lmUrlUtilService.buildActionRedirectUrl(listUrlInfo, listSpec.isTemplateList);
    listUrlInfo.reportPersonalizationUrl = this.lmUrlUtilService.buildReportPersonalizationUrl(listUrlInfo);
    listUrlInfo.listPersonalizationUrl = this.lmUrlUtilService.buildListPersonalizationUrl(listUrlInfo, listSpec);
    listUrlInfo.fieldSearchUrl = this.lmUrlUtilService.buildFieldSearchUrl(listUrlInfo);
    listUrlInfo.relatedReportSourceUrl = this.lmUrlUtilService.buildRelatedReportSourceUrl(listUrlInfo);
    listUrlInfo.actorGroupSelectUrl = this.lmUrlUtilService.buildActorGroupSelectUrl(listUrlInfo);
    listUrlInfo.roleSelectUrl = this.lmUrlUtilService.buildRoleSelectUrl(listUrlInfo);
    listUrlInfo.roleGroupSelectUrl = this.lmUrlUtilService.buildRoleGroupSelectUrl(listUrlInfo);

    // this.buildSortUrls(listUrlInfo, listSpec);

    // Append Cube parameters for cube drill
    if (listUrlInfo.url && listUrlInfo.url.searchParams.has('cubeDrill')) {
      if (this.lmContextService.useNewSpecUrls()) {
        listUrlInfo.cubeParams = this.getCubeParams(listUrlInfo.url);
      } else {
        listUrlInfo.cubeParams = (listSpec as any).cubeOptions.cubeParams;
      }
    }

    if (!this.lmContextService.useNewSpecUrls()) {
      this.compareSpecUrls(listUrlInfo, `initialDataUrl`, (listSpec as any).initialDataUrl, listUrlInfo.initialDataUrl, options);
      if (listSpec.listType !== 'filterlist') {
        this.compareSpecUrls(listUrlInfo, `transactionUrl`, (listSpec as any).transactionUrl, listUrlInfo.transactionUrl, options);
        this.compareSpecUrls(listUrlInfo, `viewAuditLogRedirectUrl`, (listSpec as any).viewAuditLogRedirectUrl, listUrlInfo.viewAuditLogRedirectUrl, options);
        this.compareSpecUrls(listUrlInfo, `navigationUrl`, (listSpec as any).navigationUrl, listUrlInfo.navigationUrl, options);
        this.compareSpecUrls(listUrlInfo, `childrenUrl`, (listSpec as any).childrenUrl, listUrlInfo.childrenUrl, options);
        this.compareSpecUrls(listUrlInfo, `centeredUrl`, (listSpec as any).centeredUrl, listUrlInfo.centeredUrl, options);
        this.compareSpecUrls(listUrlInfo, `actionRedirectUrl`, (listSpec as any).actionRedirectUrl, listUrlInfo.actionRedirectUrl, options);
        // todo openActionRedirectUrl is identical to actionRedirectUrl - remove this.
        this.compareSpecUrls(listUrlInfo, `openActionRedirectUrl`, (listSpec as any).openActionRedirectUrl, listUrlInfo.openActionRedirectUrl, options);
        this.compareSpecUrls(listUrlInfo, `reportPersonalizationUrl`, (listSpec as any).reportPersonalizationUrl, listUrlInfo.reportPersonalizationUrl, options);
        this.compareSpecUrls(listUrlInfo, `listPersonalizationUrl`, (listSpec as any).listPersonalizationUrl, listUrlInfo.listPersonalizationUrl, options);
        this.compareSpecUrls(listUrlInfo, `fieldSearchUrl`, (listSpec as any).fieldSearchUrl, listUrlInfo.fieldSearchUrl, options);
        this.compareSpecUrls(listUrlInfo, `actorGroupSelectUrl`, (listSpec as any).actorGroupSelectUrl, listUrlInfo.actorGroupSelectUrl, options);
        this.compareSpecUrls(listUrlInfo, `relatedReportSourceUrl`, (listSpec as any).relatedReportSourceUrl, listUrlInfo.relatedReportSourceUrl, options);
      }

      listUrlInfo.initialDataUrl = (listSpec as any).initialDataUrl;
      listUrlInfo.transactionUrl = (listSpec as any).transactionUrl;
      listUrlInfo.viewAuditLogRedirectUrl = (listSpec as any).viewAuditLogRedirectUrl;
      listUrlInfo.navigationUrl = (listSpec as any).navigationUrl;
      listUrlInfo.childrenUrl = (listSpec as any).childrenUrl;
      listUrlInfo.centeredUrl = (listSpec as any).centeredUrl;
      listUrlInfo.actionRedirectUrl = (listSpec as any).actionRedirectUrl;
      listUrlInfo.openActionRedirectUrl = (listSpec as any).openActionRedirectUrl;
      listUrlInfo.reportPersonalizationUrl = (listSpec as any).reportPersonalizationUrl;
      listUrlInfo.listPersonalizationUrl = (listSpec as any).listPersonalizationUrl;
      listUrlInfo.fieldSearchUrl = (listSpec as any).fieldSearchUrl;
      listUrlInfo.actorGroupSelectUrl = (listSpec as any).actorGroupSelectUrl;
      listUrlInfo.relatedReportSourceUrl = (listSpec as any).relatedReportSourceUrl;
    }
  }

  public fillOutFormUrls(formUrlInfo: LMFormUrlInfo, formSpec: LMFormSpec, formUrlOptions?: LMFormUrlInfoOptions): void {
    // crucial for mingle drill back message that contain extra valid csks, especially for csk.gen.
    // todo: need to include nc. value as well. Maybe rename this to extraParams.
    formUrlInfo.contextSessionKeys = { ...formSpec.contextSessionKeys };

    // todo spec-url-refactor not sure about how to handle buildUrls for these types.
    //   should probably handle in the build url functions themselves?!?
    // type LMFormType = 'Action' | 'Chart' | 'Composite' | 'Matrix' | 'Menu' | 'NotAForm' |
    //   'PageContext' | 'Standard' | 'Search' | 'Summary' | 'Wizard';
    if (formSpec.busFormSpec.formType === 'Menu') {
      return;
    }

    if (!this.lmContextService.useNewSpecUrls()) {
      this.compareSpecPart(formUrlInfo, 'resourceId', (formSpec as any).resourceId, formUrlInfo.resource.resourceId);
      this.compareSpecPart(formUrlInfo, 'action', (formSpec as any).action, formUrlInfo.action);

      if (formUrlInfo.sourceResource || (formSpec as any).sourceResourceType) {
        this.compareSpecPart(formUrlInfo, 'sourceResourceType', (formSpec as any).sourceResourceType, formUrlInfo.sourceResource ? formUrlInfo.sourceResource.resourceType : undefined);
        this.compareSpecPart(formUrlInfo, 'sourceResourceId', (formSpec as any).sourceResourceId, formUrlInfo.sourceResource ? formUrlInfo.sourceResource.resourceId : undefined);
      }
    }

    const buildTransactionUrlOnly = formUrlOptions && formUrlOptions.fillOutTransactionUrlOnly;

    let buildInitialDataUrl = true;
    if (buildTransactionUrlOnly || (formUrlOptions && formUrlOptions.isCompoundFieldForm)) {
      buildInitialDataUrl = false;
    }

    if (buildInitialDataUrl) {
      formUrlInfo.initialDataUrl = this.lmUrlUtilService.buildFormInitialDataUrl(formUrlInfo, formSpec);
    }

    // ---------------------------------------------------------------
    // form requests only have transaction url so don't try and build
    // all the others if this urlInfo is for a formRequest
    // ---------------------------------------------------------------
    formUrlInfo.transactionUrl = this.lmUrlUtilService.buildFormTransactionUrl(formUrlInfo, formSpec);

    if (!buildTransactionUrlOnly) {
      formUrlInfo.matrixCellTransactionUrl = this.lmUrlUtilService.buildMatrixCellTransactionUrl(formUrlInfo, formSpec);
      formUrlInfo.matrixColumnTransactionUrl = this.lmUrlUtilService.buildMatrixColumnTransactionUrl(formUrlInfo, formSpec);
      formUrlInfo.formInvokeCreateUrl = this.lmUrlUtilService.buildFormInvokeCreateUrl(formUrlInfo);
      formUrlInfo.actionRedirectUrl = this.lmUrlUtilService.buildActionRedirectUrl(formUrlInfo);
      formUrlInfo.navigationUrl = this.lmUrlUtilService.buildNavigationUrl(formUrlInfo);
      formUrlInfo.relActionUrl = this.lmUrlUtilService.buildFormRelationActionUrl(formUrlInfo);
      formUrlInfo.formRequestUrl = this.lmUrlUtilService.buildFormRequestUrl(formUrlInfo);
      formUrlInfo.viewAuditLogRedirectUrl = this.lmUrlUtilService.buildViewAuditLogRedirectUrl(formUrlInfo);
      formUrlInfo.previewActionUrl = this.lmUrlUtilService.buildPreviewActionUrl(formUrlInfo);
    }

    if (!this.lmContextService.useNewSpecUrls()) {
      if (buildInitialDataUrl) {
        // ---------------------------------------------------------------
        // if not building initialDataUrl on client then don't compare
        // against one delivered from server.
        // ---------------------------------------------------------------
        this.compareSpecUrls(formUrlInfo, 'initialDataUrl', (formSpec as any).initialDataUrl, formUrlInfo.initialDataUrl, formUrlOptions);
        formUrlInfo.initialDataUrl = (formSpec as any).initialDataUrl;
      }

      this.compareSpecUrls(formUrlInfo, 'transactionUrl', (formSpec as any).transactionUrl, formUrlInfo.transactionUrl, formUrlOptions);
      formUrlInfo.transactionUrl = (formSpec as any).transactionUrl;

      if (!buildTransactionUrlOnly) {
        this.compareSpecUrls(formUrlInfo, 'matrixCellTransactionUrl', (formSpec as any).matrixCellTransactionUrl, formUrlInfo.matrixCellTransactionUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'matrixColumnTransactionUrl', (formSpec as any).matrixColumnTransactionUrl, formUrlInfo.matrixColumnTransactionUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'formInvokeCreateUrl', (formSpec as any).formInvokeCreateUrl, formUrlInfo.formInvokeCreateUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'actionRedirectUrl', (formSpec as any).actionRedirectUrl, formUrlInfo.actionRedirectUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'navigationUrl', (formSpec as any).navigationUrl, formUrlInfo.navigationUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'relActionUrl', (formSpec as any).relActionUrl, formUrlInfo.relActionUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'formRequestUrl', (formSpec as any).formRequestUrl, formUrlInfo.formRequestUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'viewAuditLogRedirectUrl', (formSpec as any).viewAuditLogRedirectUrl, formUrlInfo.viewAuditLogRedirectUrl, formUrlOptions);
        this.compareSpecUrls(formUrlInfo, 'previewActionUrl', (formSpec as any).previewActionUrl, formUrlInfo.previewActionUrl, formUrlOptions);

        formUrlInfo.matrixCellTransactionUrl = (formSpec as any).matrixCellTransactionUrl;
        formUrlInfo.matrixColumnTransactionUrl = (formSpec as any).matrixColumnTransactionUrl;
        formUrlInfo.formInvokeCreateUrl = (formSpec as any).formInvokeCreateUrl;
        formUrlInfo.actionRedirectUrl = (formSpec as any).actionRedirectUrl;
        formUrlInfo.navigationUrl = (formSpec as any).navigationUrl;
        formUrlInfo.relActionUrl = (formSpec as any).relActionUrl;
        formUrlInfo.formRequestUrl = (formSpec as any).formRequestUrl;
        formUrlInfo.viewAuditLogRedirectUrl = (formSpec as any).viewAuditLogRedirectUrl;
        formUrlInfo.previewActionUrl = (formSpec as any).previewActionUrl;
      }

      formUrlInfo.navigationContext = (formSpec as any).navigationContext;
      formUrlInfo.fromInbox = (formSpec as any).fromInbox;
      formUrlInfo.processFlow =  (formSpec as any).processFlow;
    }
  }

  public getBaseUrlInfoFromHttpUrl(httpUrl: string, urlInfoOptions?: LMUrlInfoOptions): LMBaseUrlInfo {

    if (!window.location.origin) {
      // -----------------------------------------------------------------------------
      // Remove this once we stop supporting IE11
      // -----------------------------------------------------------------------------
      (window.location as any).origin = window.location.protocol + '//'
        + window.location.hostname
        + (window.location.port ? ':' + window.location.port : '');
    }

    const url: URL = this.lmUrlUtilService.buildURLFromHref(httpUrl);
    const basePath = this.buildBasePathFromURL(url);
    // not sure why we would ever use buildBasePathFromLocation. The url passed in
    // is the correct one to get teh basePath from.
    const isGenUrl: boolean = this.lmUrlUtilService.isGenUrlUsingURL(url);
    // const basePath = isGenUrl ? this.buildBasePathFromURL(url) : this.buildBasePathFromLocation();
    const resource = this.getResource(url.pathname, isGenUrl);
    const sourceResource = this.getSourceResource(url.searchParams);
    const filterId = url.searchParams.get('filterId');

    let fromListResource: KeyedResource;
    if (sourceResource) {
      fromListResource = this.getResourceFromParams(url.searchParams, 'fromlist');
    }

    const listResource = this.getResourceFromParams(url.searchParams, 'list');
    const action = url.searchParams.get('action') || undefined;

    const navigationContext = this.getNavigationContextFromParams(url);
    const fromInbox = url.searchParams.get('fromInbox') === 'true';
    const processFlow = url.searchParams.get('processFlow') === 'true';

    let specUrl: string;
    if (this.lmContextService.useNewSpecUrls()) {
      specUrl = this.getSpecUrl(url, basePath, resource, action, urlInfoOptions);
    } else {
      specUrl = url.href.split('#')[0];
      specUrl = this.lmUrlUtilService.updateUrlParams(specUrl, { output: 'spec' });
    }

    const urlInfo: LMBaseUrlInfo = {
      url,
      basePath,
      specUrl,
      resource,
      sourceResource,
      listResource,
      fromListResource,
      action,
      navigationContext,
      fromInbox,
      processFlow,
      filterId
    };
    return urlInfo;
  }

  private getBaseUrlInfoFromListSpec(listSpec: LMListSpec, options?: LMListUrlInfoOptions): LMBaseUrlInfo {
    const basePath = this.buildBasePathFromLocation();
    const resource = new KeyedResource({ resourceType: 'list', resourceId: listSpec.resourceId });

    let sourceResource;
    if (options?.pagePaneSpec?.relationResourceId) {
      sourceResource = new KeyedResource({ resourceType: 'relation', resourceId: options.pagePaneSpec.relationResourceId });
    } else if (listSpec.sourceResourceType && listSpec.sourceResourceId) {
      sourceResource = new KeyedResource({ resourceType: listSpec.sourceResourceType, resourceId: listSpec.sourceResourceId });
    }

    const urlInfo: LMBaseUrlInfo = { basePath, resource, sourceResource };
    this.fillOutListUrls(urlInfo, listSpec, options);
    return urlInfo;
  }

  private getBaseUrlInfoFromFormSpec(formSpec: LMFormSpec, options?: LMFormUrlInfoOptions): LMBaseUrlInfo {
    const basePath = this.buildBasePathFromLocation();
    const resource = new KeyedResource({ resourceType: 'form', resourceId: formSpec.resourceId });

    // const action = options ? && options.formPanelActionIs ? '{actionName}' : undefined;
    let action;
    if (options) {
      if (options.formPanelActionIs) {
        action = '{actionName}';
      } else if (options.fillOutTransactionUrlOnly) {
        action = formSpec.action; // todo spec-url-refactor may need to undeprecate this just for the form-request.service scenario
      }
    }

    let sourceResource;
    if (formSpec.sourceResourceType && formSpec.sourceResourceType) {
      sourceResource = new KeyedResource({ resourceType: formSpec.sourceResourceType, resourceId: formSpec.sourceResourceId });
    }

    const urlInfo: LMBaseUrlInfo = { basePath, resource, sourceResource, action };
    if (options && options.skipFilloutUrls) {
      return urlInfo;
    }

    if (formSpec.isImplicit) {
      // --------------------------------------------------------------------------------
      // isImplicit is used for formSpecs that are built on the fly on the client side.
      // the do not contain urls so don't fill out urls here.
      // --------------------------------------------------------------------------------
      return urlInfo;
    }

    this.fillOutFormUrls(urlInfo, formSpec, options);
    return urlInfo;
  }

  private getSpecUrl(url: URL, basePath: string, resource: KeyedResource, action: string, urlInfoOptions?: LMUrlInfoOptions): string {
    // -----------------------------------------------------------------------
    // build the resource object to use in removing the keys section.
    // -----------------------------------------------------------------------
    const specResourceId = resource.busClassName + (resource.uiResourceName ? '.' + resource.uiResourceName : '');

    // -----------------------------------------------------------------------
    // now we can build our spec url path.
    // -----------------------------------------------------------------------
    let specUrl = basePath + '/' + resource.resourceType + '/' + specResourceId;
    const urlParams: LMUrlParams = { output: 'spec', action };

    if (urlInfoOptions && urlInfoOptions.includeNavResourceIsSpecUrl) {
      if (this.getSourceResourceType(url.searchParams) === AppConstants.RESOURCETYPE_NAVIGATION) {
        urlParams[AppConstants.RESOURCETYPE_NAVIGATION] = url.searchParams.get(AppConstants.RESOURCETYPE_NAVIGATION);
      }
    }

    // ----------------------------------------------------------------------------------------------
    // User folder will return a different spec so include it in the spec url.
    // ----------------------------------------------------------------------------------------------
    if (url && url.searchParams.has('userfolder')) {
      urlParams.userfolder = url.searchParams.get('userfolder') || undefined;
    }

    // ----------------------------------------------------------------------------------------------
    // Not really used on server, just here so that the spec url in unique by financeEnterpriseGroup
    // for the purpose of url/response caching of jsonSpecs for both Conditional GETs and "edge caching".
    // EXPLANATION: For FEGs the server will return different CodeBlock specs.
    // ----------------------------------------------------------------------------------------------
    urlParams._feg = this.lmContextService.getContext().financeEnterpriseGroup;

    // ----------------------------------------------------------------------------------------------
    // must include any csks that will return different specs, for now adding all csks from
    // global context so we don't break anything.
    // todo look to include only those csks that coudl return a different spec.
    // ----------------------------------------------------------------------------------------------
    const globalContextCsks = this.lmContextService.getContext().contextSessionKeys;
    Object.keys(globalContextCsks).forEach(key => urlParams[key] = globalContextCsks[key]);

    // ------------------------------------------------------------------------------
    // for mingle drillback and custom code urls have to propogate their urls csks
    // so we don't break backward compatibility.
    //
    // todo: The goal is to disallow csks from drillback and custom code urls but
    //  we cannot at the moment due to the risk of breaking customers.
    // ------------------------------------------------------------------------------
    const urlCsks: Params = this.lmUrlUtilService.getPrefixedParams(url.href, 'csk.');
    Object.keys(urlCsks).map(key => urlParams[key] = urlCsks[key]);

    specUrl = this.lmUrlUtilService.updateUrlParams(specUrl, urlParams);
    return specUrl;
  }

  private getResource(path: string, isGenUrl: boolean): KeyedResource {
    const resourceType = this.lmUrlUtilService.getUrlPathValue(path, AppConstants.URLSEG_RESOURCE_TYPE_INDEX, isGenUrl);
    const resourceId = this.lmUrlUtilService.getUrlPathValue(path, AppConstants.URLSEG_RESOURCE_ID_INDEX, isGenUrl);
    const resource: KeyedResource = new KeyedResource({ resourceType, resourceId });
    return resource;
  }

  /**
   * Get an object of the source resourceType and id strings by searching in the url params.
   * @param an object of resourceType and resourceId
   */
  private getSourceResource(urlParams: URLSearchParams): Resource {
    const resourceType = this.getSourceResourceType(urlParams);
    if (resourceType === null) {
      return null;
    }

    const resourceId = urlParams.get(resourceType);
    if (resourceType === 'bidrill') {
      return new BIDrillResource( { resourceId });
    }
    return new KeyedResource({ resourceType, resourceId });
  }

  private getResourceFromParams(urlParams: URLSearchParams, resourceType: string): KeyedResource {
    const resourceId = urlParams.get(resourceType);
    if (!resourceId) {
      return null;
    }

    return new KeyedResource({ resourceType, resourceId });
  }

  /**
   * Get the source resource type by looking specifically for navigation, drill, and
   * relation in the url params keys.
   * @param urlParams
   */
  private getSourceResourceType(urlParams: URLSearchParams): string {
    if (urlParams.has(AppConstants.RESOURCETYPE_BIDRILL)) {
      return AppConstants.RESOURCETYPE_BIDRILL;
    }

    if (urlParams.has(AppConstants.RESOURCETYPE_DRILL)) {
      return AppConstants.RESOURCETYPE_DRILL;
    }

    if (urlParams.has(AppConstants.RESOURCETYPE_FORMINVOKE)) {
      return AppConstants.RESOURCETYPE_FORMINVOKE;
    }

    if (urlParams.has(AppConstants.RESOURCETYPE_LIST)) {
      return AppConstants.RESOURCETYPE_LIST;
    }

    if (urlParams.has(AppConstants.RESOURCETYPE_MENU)) {
      return AppConstants.RESOURCETYPE_MENU;

    }
    if (urlParams.has(AppConstants.RESOURCETYPE_NAVIGATION)) {
      return AppConstants.RESOURCETYPE_NAVIGATION;
    }

    if (urlParams.has(AppConstants.RESOURCETYPE_RELATION)) {
      return AppConstants.RESOURCETYPE_RELATION;
    }

    if (urlParams.has(AppConstants.RESOURCETYPE_SELECT)) {
      return AppConstants.RESOURCETYPE_SELECT;
    }

    if (urlParams.has(AppConstants.RESOURCETYPE_PAGEMENU)) {
      return AppConstants.RESOURCETYPE_PAGEMENU;
    }

    if (urlParams.has(AppConstants.RESOURCETYPE_PAGEPANEL)) {
      return AppConstants.RESOURCETYPE_PAGEPANEL;
    }

    return null;
  }

  private buildBasePathFromURL(url: URL): string {
    const isGenUrl: boolean = this.lmUrlUtilService.isGenUrlUsingURL(url);
    const webApp = this.lmUrlUtilService.getUrlPathValue(url.pathname, AppConstants.URLSEG_WEBAPP_INDEX, isGenUrl);
    if (isGenUrl || this.lmContextService.isGenProductLine()) {
      return '/' + webApp;
    }

    const dataArea = this.lmUrlUtilService.getUrlPathValue(url.pathname, AppConstants.URLSEG_PDL_OR_DA_INDEX, isGenUrl);
    return '/' + dataArea + '/' +  webApp;
  }

  private buildBasePathFromLocation(): string {
    // need to get base path from location since the global context isn't always changed when running
    // a url. For example when searching for something in the app menu and clicking a menu item from
    // another web app.
    const isGenUrl: boolean = this.lmUrlUtilService.isGenUrlUsingWindowLocation();

    const webApp = this.lmUrlUtilService.getUrlPathValue(window.location.pathname, AppConstants.URLSEG_WEBAPP_INDEX, isGenUrl);
    if (isGenUrl || this.lmContextService.isGenProductLine()) {
      return '/' + webApp;
    }

    const dataArea = this.lmUrlUtilService.getUrlPathValue(window.location.pathname, AppConstants.URLSEG_PDL_OR_DA_INDEX, isGenUrl);
    return '/' + dataArea + '/' +  webApp;
  }

  private getNavigationContextFromParams(url: URL): LMNavContextKeys {
    const navigationContext: LMNavContextKeys = {};
    url.searchParams.forEach((value, key) => {
      if (!key.startsWith('nc.')) {
        return;
      }
      navigationContext[key] = value;
    });

    return navigationContext;
  }

  private getCubeParams(url: URL): LMCubeUrlParams {
    const cubeParams: LMCubeUrlParams = {};
    cubeParams.cubeDrill = true;
    cubeParams.isFactRowElement = url.searchParams.get('isFactRowElement') === 'true';
    cubeParams.cubeMediator = url.searchParams.get('cubeMediator');
    cubeParams.cubeNm = url.searchParams.get('cubeNm');
    cubeParams.cubeFld = url.searchParams.get('cubeFld');

    if (url.searchParams.has('addtlFilter')) {
      cubeParams.addtlFilter = url.searchParams.get('addtlFilter');
    }
    if (url.searchParams.has('reportingBasis')) {
      cubeParams.reportingBasis = url.searchParams.get('reportingBasis');
    }
    if (url.searchParams.has('periodKey')) {
      cubeParams.periodKey = url.searchParams.get('periodKey');
    }
    if (url.searchParams.has('periodView')) {
      cubeParams.periodView = url.searchParams.get('periodView');
    }
    if (url.searchParams.has('pvPeriodIdx')) {
      cubeParams.pvPeriodIdx = parseInt(url.searchParams.get('pvPeriodIdx'), 10);
    }

    for (let i = 0; ; i++) {
      const dimId = url.searchParams.get('topDimId_' + i) || undefined;
      if (dimId === undefined) {
        break;
      }
      const dimVal = url.searchParams.get('topDimVal_' + i);
      if (i === 0) {
        cubeParams.topNodeDims = [];
      }
      cubeParams.topNodeDims.push({ topDimId: dimId, topDimVal: dimVal });
    }

    for (let i = 0; ; i++) {
      const topcFldId = url.searchParams.get('topcFldId_' + i) || undefined;
      if (topcFldId === undefined) {
        break;
      }
      const topcFldVal = url.searchParams.get('topcFldVal_' + i);
      if (i === 0) {
        cubeParams.topContextFields = [];
      }
      cubeParams.topContextFields.push({ topcFldId, topcFldVal });
    }

    for (let i = 0; ; i++) {
      const dimId = url.searchParams.get('dimId_' + i) || undefined;
      if (dimId === undefined) {
        break;
      }
      const dimVal = url.searchParams.get('dimVal_' + i);
      const dimFixed = url.searchParams.get('dimFixed_' + i);
      const dimTotal = url.searchParams.get('dimTotal_' + i);
      const dimType = url.searchParams.get('dimType_' + i);
      if (i === 0) {
        cubeParams.dimensions = [];
      }
      cubeParams.dimensions.push({ dimId, dimVal, dimFixed, dimTotal, dimType });
    }

    for (let i = 0; ; i++) {
      const dimId = url.searchParams.get('ctxDimId_' + i) || undefined;
      if (dimId === undefined) {
        break;
      }
      const dimVal = url.searchParams.get('ctxDimVal_' + i);
      const dimFixed = url.searchParams.get('ctxDimFixed_' + i);
      const dimTotal = url.searchParams.get('ctxDimTotal_' + i);
      const dimType = url.searchParams.get('ctxDimType_' + i);
      if (i === 0) {
        cubeParams.ctxDimensions = [];
      }
      cubeParams.ctxDimensions.push({ ctxDimId: dimId, ctxDimVal: dimVal, ctxDimFixed: dimFixed, ctxDimTotal: dimTotal, ctxDimType: dimType });
    }

    for (let i = 0; ; i++) {
      const dimId = url.searchParams.get('fltrDimId_' + i) || undefined;
      if (dimId === undefined) {
        break;
      }
      const dimVal = url.searchParams.get('fltrDimVal_' + i);
      const dimFixed = url.searchParams.get('fltrDimFixed_' + i);
      const dimTotal = url.searchParams.get('fltrDimTotal_' + i);
      const dimType = url.searchParams.get('fltrDimType_' + i);
      if (i === 0) {
        cubeParams.fltrDimensions = [];
      }
      cubeParams.fltrDimensions.push({ fltrDimId: dimId, fltrDimVal: dimVal, fltrDimFixed: dimFixed, fltrDimTotal: dimTotal, fltrDimType: dimType });
    }

    return cubeParams;
  }

  public compareSpecUrls(baseUrlInfo: LMBaseUrlInfo, name: string, serverHref: string, clientHref: string, compareOptions?: LMCompareUrlOptions) {
    try {
      if (!serverHref && !clientHref) {
        return; // no op if they are both empty.
      }

      const clientUrl = this.lmUrlUtilService.buildURLFromHref(clientHref);
      const serverUrl = this.lmUrlUtilService.buildURLFromHref(serverHref);

      const diffs: string[][] = [];
      if (serverUrl && clientUrl) {
        this.comparePathname(diffs, clientUrl.pathname, serverUrl.pathname);
        this.compareUrlParams(diffs, baseUrlInfo, clientUrl, serverUrl, compareOptions);
      } else {
        diffs.push([
          `clientUrl: ${clientHref}`,
          `serverUrl: ${serverHref}`
        ]);
      }

      if (diffs.length > 0) {
        this.processCompareDiffs(name, diffs, baseUrlInfo, clientUrl, serverUrl);
      }

    } catch (error) {
      const jsonLog = {
        message: `${LMUrlInfoBuilderService.ServerLogVersionId}: ${LMUrlInfoBuilderService.PrefixString}: Exception in compareSpecUrls: ${name} - ${error.stack}`,
        clientUrl: `${serverHref}`,
        serverUrl: `${clientHref}`,
        browserHref: window.location.href,
        referrer: document.referrer,
        urlInfo: this.baseUrlData(baseUrlInfo)
      };

      console.error(jsonLog);
      this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
    }
  }

  private paramsAreEqual(paramName: string, clientParams: string[], serverParams: string[]) {
    if (clientParams.length !== serverParams.length) {
      return false;
    }

    let sortedClientParams: string[] = clientParams.sort();
    const sortedServerParams: string[] = serverParams.sort();

    if (paramName === 'fld') {
      sortedClientParams = sortedClientParams.map((clientParam: string, index: number) => {
        const serverParam = sortedServerParams[index];

        if (clientParam === serverParam
          ||  clientParam.indexOf('_col_') < 0) {
          return clientParam;
        }

        const newClientParam = clientParam.replace('_col_', '_prd_');
        if (newClientParam === serverParam) {
          return newClientParam;
        }

        return clientParam;
      });
    }

    return _.isEqual(sortedClientParams, sortedServerParams);
  }

  private compareSpecPart(baseUrlInfo: LMBaseUrlInfo, partName: string, serverThing: string, clientThing: string): boolean {
    if (!serverThing && !serverThing) {
      return true; // so undefined and null will equate
    }

    let result;
    if (serverThing && clientThing) {
      result = serverThing.toLowerCase() === clientThing.toLowerCase();
    } else {
      result = serverThing === clientThing;
    }

    if (result) {
      return true;
    }

    const jsonLog = {
      message: `${LMUrlInfoBuilderService.ServerLogVersionId}: ${LMUrlInfoBuilderService.PrefixString}: Found differences in Spec vs UrlInfo: ${partName}`,
      client: `${partName}: ${clientThing}`,
      server: `${partName}: ${serverThing}`,
      browserHref: window.location.href,
      referrer: document.referrer,
      urlInfo: this.baseUrlData(baseUrlInfo)
    };

    console.error(jsonLog);
    this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
    return false;
  }

  // private buildSortUrls(listUrlInfo: LMListUrlInfo, listSpec: LMListSpec) {
  //   if (!listSpec.busListSpec.displayFields || listSpec.busListSpec.displayFields.length === 0) {
  //     return;
  //   }
  //
  //   listSpec.busListSpec.displayFields.forEach(displayField => {
  //     if (displayField.cardViewSortOrders && displayField.cardViewSortOrders.length > 0) {
  //       displayField.cardViewSortOrders.forEach(cardViewSortOrder => {
  //         const sortUrl = this.lmUrlUtilService.buildSortUrl(listUrlInfo, listSpec, cardViewSortOrder.sortOrderName, cardViewSortOrder.isAscending);
  //
  //         if (this.lmContextService.useNewSpecUrls()) {
  //           cardViewSortOrder.sortUrl = sortUrl;
  //         } else if (this.lmContextService.compareSpecUrls()) {
  //           this.compareSpecUrls(displayField.fieldId + '.cardViewSortOrder', cardViewSortOrder.sortUrl, sortUrl);
  //         }
  //       });
  //
  //     } else if (displayField.sortOrderName) {
  //       const sortUrl = this.lmUrlUtilService.buildSortUrl(listUrlInfo, listSpec, displayField.sortOrderName, displayField.isAscending);
  //
  //       if (this.lmContextService.useNewSpecUrls()) {
  //         displayField.sortUrl = sortUrl;
  //       } else if (this.lmContextService.compareSpecUrls()) {
  //         this.compareSpecUrls(displayField.fieldId + '.sortUrl', displayField.sortUrl, sortUrl);
  //       }
  //     }
  //   });
  // }

  private baseUrlData(fullUrlInfo: LMBaseUrlInfo): any {
    const baseUrlData = {
      basePath: fullUrlInfo.basePath,
      // url: fullUrlInfo.url,
      resource: fullUrlInfo.resource ? fullUrlInfo.resource.resourceId : '',
      sourceResource: fullUrlInfo.sourceResource ? fullUrlInfo.sourceResource.resourceId : '',
      listResource: fullUrlInfo.listResource ? fullUrlInfo.listResource.resourceId : '',
      fromListResource: fullUrlInfo.fromListResource ? fullUrlInfo.fromListResource.resourceId : '',
      action: fullUrlInfo.action,
      navigationContext: fullUrlInfo.navigationContext,
      fromInbox: fullUrlInfo.fromInbox,
      processFlow: fullUrlInfo.processFlow,
    };

    return baseUrlData;
  }

  private formatDiffs(diffs: string[][]): string {
    try {
      const diffString: string[] = diffs.map(diff => `["${diff[0]}","${diff[1]}"]`);
      return diffString.join(',');
    } catch (e) {
      return 'failed formatting diff';
    }
  }

  private comparePathname(
    diffs: string[][],
    clientPathname: string,
    serverPathname: string
  ) {
    if (clientPathname !== serverPathname) {
      diffs.push([
        `clientUrl pathname: ${clientPathname}`,
        `serverUrl pathname: ${serverPathname}`,
      ]);
    }
  }

  private static paramsToIgnore = [
    // 'csk.ng',
    // 'csk.cli',
    // 'csk.showusingxi',
    // 'csk.ng3x',
    // 'csk.displayType',
    // 'csk.IsoLocale',
    // 'csk.JobBoard',
    // 'csk.HROrganization',
  ];

  private compareUrlParams(
    diffs: string[][],
    baseUrlInfo: LMBaseUrlInfo,
    clientUrl: URL,
    serverUrl: URL,
    compareOptions: LMCompareUrlOptions
  ): void {
    const clientUrlParamKeys: string[] = [];
    const serverUrlParamKeys: string[] = [];

    if (compareOptions && compareOptions.comparePathAndSourceResourceOnly) {
      if (baseUrlInfo.sourceResource) {
        clientUrlParamKeys.push(baseUrlInfo.sourceResource.resourceType);
      }
    } else {
      clientUrl.searchParams.forEach((value, key) => {
        if (LMUrlInfoBuilderService.paramsToIgnore.find(pti => pti === key)) {
          return;
        }

        clientUrlParamKeys.push(key);
      });

      serverUrl.searchParams.forEach((value, key) => {
        if (LMUrlInfoBuilderService.paramsToIgnore.find(pti => pti === key)) {
          return;
        }

        serverUrlParamKeys.push(key);
      });
    }

    const allUrlParamKeys = new Set([ ... clientUrlParamKeys, ... serverUrlParamKeys ]);
    allUrlParamKeys.forEach(key => {
      const clientParams: string[] = clientUrl.searchParams.getAll(key);
      const serverParams: string[] = serverUrl.searchParams.getAll(key);
      if (!this.paramsAreEqual(key, clientParams, serverParams)) {
        diffs.push([
          `clientUrlParam: ${key}=${clientParams}`,
          `serverUrlParam: ${key}=${serverParams}`
        ]);
      }
    });
  }

  private processCompareDiffs(name: string, diffs: string[][], baseUrlInfo: LMBaseUrlInfo, clientUrl: URL, serverUrl: URL): void {
    if (diffs.length === 0) {
      return;
    }

    const jsonLog = {
      message: `${LMUrlInfoBuilderService.ServerLogVersionId}: ${LMUrlInfoBuilderService.PrefixString}: Found differences in url: ${name}`,
      diffs,
      clientUrl,
      serverUrl,
      browserHref: window.location.href,
      referrer: document.referrer,
      urlInfo: this.baseUrlData(baseUrlInfo)
    };

    console.error(jsonLog);
    this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
  }

  public processDrillbackAndCustomCodeUrl(url: string, name: string): string {
    // if it's a drill back url the logical id is already set in the mingle plugin.
    url = this.lmUrlUtilService.removeParams(url, [ 'csk.LogicalId' ]);

    this.logUnexpectedUrlParams(url, name);
    url = this.removeBadDrillbackParams(url);
    return url;
  }

  /**
   * These params are not allowed to change the mingle plugin context.
   * These params are also not allowed to change the standard client context
   * when using the the custom code callback.
   */
  private static badDrillBackParams = [
    'csk.ng3x',
    'csk.ng',
    'csk.cli',
    'csk.3x',
    'csk.developer',
    'csk.showusingxi',
    'csk.hidetitlebar',
    'csk.hidemenubar',
    'csk.displayType',
  ];
  // 'csk.IsoLocale',
  // 'csk.inforThemeName',
  // 'csk.disableuiconfigs',

  // todo talk with Laura Henkey about their needs for csk.IsoLocale.
  // todo: Laura Kenkey suggest talking with Lynn Rivard and Barbie Sokol
  //  about how mingle drillbacks might be using these.
  // 'csk.JobBoard',
  // 'csk.HROrganization',

  private static validDrillBackParams = [
    'csk.gen',
  ];

  /**
   * This method removes "bad" or unsupported mingle and custom code
   * drill back params.
   */
  private removeBadDrillbackParams(url: string): string {
    // --------------------------------------------------------------------------------
    // removing bad params from the drillback url will stop compareSpec from
    // failing when comparing client-build-urls and server-built-urls.
    // it will also stop the csks from getting into the extra spec.contextSessionKeys
    // object, built on the server, and make sure they don't get used in any url.
    // --------------------------------------------------------------------------------
    // todo holding off removed params from mingle drillbacks until we know
    //  that no customer is relying on them.
    // return this.lmUrlUtilService.removeParams(url, LMUrlInfoBuilderService.badDrillBackParams);
    return url;
  }

  private logUnexpectedUrlParams(url: string, type: string): void {
    try {
      const cskParamsNotInContext = this.lmUrlUtilService.getCskParamsNotInContext(url, LMUrlInfoBuilderService.validDrillBackParams);
      if (_.isEmpty(cskParamsNotInContext)) {
        return;
      }

      const jsonLog = {
        message: `${LMUrlInfoBuilderService.ServerLogVersionId}: ${type} URL ERROR: Found csks not in global ui context`,
        cskParamsNotInContext,
        url,
        type,
        referrer: document.referrer
      };

      console.error(jsonLog);
      this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
    } catch (error) {
      const jsonLog = {
        message: `${LMUrlInfoBuilderService.ServerLogVersionId}: ${type} URL ERROR: Exception in logUnexpectedUrlParams`,
        stacktrace: `${error.stack}`,
        referrer: document.referrer
      };

      console.error(jsonLog);
      this.lmLoggerService.logToServerConsole(JSON.stringify(jsonLog));
    }
  }
}
